import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '@material-ui/core';
import { Info as InfoIcon } from '@material-ui/icons';

import { FetchError } from '../../../common/api/api';
import CardTitle from '../../../common/card/CardTitle';
import CardContent from '../../../common/card/CardContent';
import InlineForm from '../../../common/inline/InlineForm';
import InlineHtml from '../../../common/inline/InlineHtml';
import InlineFormik from '../../../common/inline/InlineFormik';
import { InlineFileValue } from '../../../common/inline/InlineFile';
import InlineTextField from '../../../common/inline/InlineTextField';
import { useLinkTransform } from '../../../common/HeaderBackButton';
import InlineSelect from '../../../common/inline/InlineSelect';
import { useUpdateTask } from '../../tasks/Task.hooks';
import { useUpdateProductDesignOutput } from '../Product.hooks';
import {
  ProductDesignOutput,
  BaseProductDesignOutputCardProps
} from '../Product.interface';
import { useTaskEntityFields } from '../../workflow/Workflow.hooks';
import { getProductEntityName } from '../Product.config';
import environment from '../../../environments';

const DesignOutputInfoCard: React.FC<BaseProductDesignOutputCardProps> = (
  props: BaseProductDesignOutputCardProps
) => {
  const {
    title = `Product ${getProductEntityName('designOutput')} Info`,
    productId,
    productDesignOutput,
    productDesignOutputVersion,
    hidden = false,
    enableTitleRedirect = false,
    onChange,
    onUpdate,
    onError
  } = props;
  const initialData = productDesignOutputVersion || productDesignOutput;
  const initialValues: Partial<ProductDesignOutput> = {
    title: initialData.title || '',
    essentialRequirements: initialData.essentialRequirements || '',
    description: initialData.description || ''
  };
  const getPath = useLinkTransform(
    `/portal/products/${productId}/design-outputs/${productDesignOutput.id}`,
    [{ query: 'from' }]
  );
  const {
    updatedProductDesignOutput,
    isUpdatingProductDesignOutput: isUpdating,
    updateProductDesignOutputError: updateError,
    updateProductDesignOutput
  } = useUpdateProductDesignOutput(productId, productDesignOutput.id);
  const {
    updatedTask,
    updateError: updateTaskError,
    updateTask
  } = useUpdateTask(productDesignOutput.task.id || 0);
  const { isFieldDisabled } = useTaskEntityFields(productDesignOutput.task);
  const error: FetchError | undefined = updateError || updateTaskError;

  useEffect(() => {
    if (updatedProductDesignOutput) {
      onChange?.(updatedProductDesignOutput);
      onUpdate?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatedProductDesignOutput]);

  useEffect(() => {
    if (updatedTask) {
      productDesignOutput.task = Object.assign(
        productDesignOutput.task || {},
        updatedTask
      );
      onChange?.(productDesignOutput);
      onUpdate?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatedTask]);

  useEffect(() => {
    if (error) {
      onError?.(error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  return (
    <Card style={hidden ? { display: 'none' } : undefined}>
      <CardTitle>
        <InfoIcon />
        {enableTitleRedirect ? (
          <Link className='link' to={getPath()}>
            {title}
          </Link>
        ) : (
          title
        )}
      </CardTitle>
      <CardContent padding='large'>
        <InlineFormik
          error={updateError}
          isSubmitting={isUpdating}
          initialValues={initialValues}
          onSubmit={(values: Partial<ProductDesignOutput>) => {
            updateProductDesignOutput(values);
            onChange?.(values);
          }}
        >
          <InlineForm>
            <InlineTextField
              disabled
              name='productId'
              label='Product ID'
              value={`PRODUCT-${productId}`}
            />
            <InlineTextField
              name='title'
              label='Title'
              placeholder={`Enter title for Product ${getProductEntityName(
                'designOutput'
              )}`}
              disabled={isUpdating || isFieldDisabled('title')}
            />
            {environment.productConfig?.designInput?.useCustomInfoFields && (
              <InlineSelect
                name='essentialRequirements'
                label='Essential Requirements (Y/N)'
                disabled={
                  isUpdating || isFieldDisabled('essentialRequirements')
                }
                options={[
                  { value: 'yes', label: 'Y' },
                  { value: 'no', label: 'N' }
                ]}
              />
            )}
            <InlineHtml
              name='description'
              label='Description'
              helperText={`Enter description for Product ${getProductEntityName(
                'designOutput'
              )}`}
              disabled={isUpdating || isFieldDisabled('description')}
              attachments={productDesignOutput.task.attachments || []}
              onChangeAttachments={(attachments: InlineFileValue[]) => {
                updateTask({ attachments });
                if (productDesignOutput.task) {
                  productDesignOutput.task.attachments = attachments;
                }
                onChange?.(productDesignOutput);
              }}
            />
          </InlineForm>
        </InlineFormik>
      </CardContent>
    </Card>
  );
};

export default DesignOutputInfoCard;
